import clsx from 'clsx';
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import IconChevronDown from '@/assets/icons/icon-arrow-down-01.svg?react';
import IconClose from '@/assets/icons/icon-close.svg?react';
import { computeFloatingPosition } from '@/utils/computeFloatingPosition';

interface DatePickerProps {
  text?: string;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
}

const DAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const POPUP_WIDTH = 280;
const POPUP_HEIGHT = 380;

/**
 * Parse ISO date string in local timezone to avoid off-by-one day errors
 * "2024-01-15" should be Jan 15 in local time, not UTC
 */
const parseLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const DatePicker = ({ text, value, onChange, className }: DatePickerProps) => {
  const [viewDate, setViewDate] = useState(() => {
    return value ? parseLocalDate(value) : new Date();
  });
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const selectedDate = value ? parseLocalDate(value) : null;
  const isOpen = pos !== null;

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target as Node)
      ) {
        setPos(null);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const formatDisplayDate = (dateString?: string) => {
    if (!dateString) return 'Select date';
    const date = parseLocalDate(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    // Monday = 0, Sunday = 6
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;

    const days: { date: Date; isCurrentMonth: boolean }[] = [];

    // Previous month days
    const prevMonth = new Date(year, month, 0);
    for (let i = startDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false,
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true,
      });
    }

    // Next month days - only fill to complete the last row
    const totalDays = days.length;
    const remaining = totalDays % 7 === 0 ? 0 : 7 - (totalDays % 7);
    for (let i = 1; i <= remaining; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false,
      });
    }

    return days;
  };

  const handlePrevMonth = (e: React.MouseEvent) => {
    e.stopPropagation();
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const handleNextMonth = (e: React.MouseEvent) => {
    e.stopPropagation();
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const handleSelectDate = (date: Date, e: React.MouseEvent) => {
    e.stopPropagation();
    // Prevent selecting past dates
    if (isPastDate(date)) {
      return;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const isoDate = `${year}-${month}-${day}`;
    onChange?.(isoDate);
    setPos(null);
  };

  const isSelected = (date: Date) => {
    if (!selectedDate) return false;
    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  const isPastDate = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate < today;
  };

  const days = getDaysInMonth(viewDate);
  const monthYear = `${viewDate.toLocaleDateString('en-US', { month: 'long' })} (${viewDate.getFullYear()})`;

  const handleToggle = () => {
    if (pos) {
      setPos(null);
      return;
    }
    const inspectorEl = anchorRef.current?.closest('.inspector') as HTMLElement | null;
    const p = inspectorEl
      ? computeFloatingPosition({
          inspectorEl,
          anchorEl: anchorRef.current,
          width: POPUP_WIDTH,
          height: POPUP_HEIGHT,
          preferSide: 'left',
        })
      : null;
    if (p) setPos({ top: p.top, left: p.left });
  };

  return (
    <div ref={anchorRef} className={clsx('flex w-full items-center justify-between', className)}>
      {text && <p className="text-button-sm font-medium text-neutral-500">{text}</p>}

      <button
        type="button"
        onClick={handleToggle}
        className={clsx(
          'text-button-sm flex h-8 items-center gap-2 rounded-md px-3 font-medium text-neutral-700 shadow-[inset_0px_0px_2px_0px_rgba(0,0,0,0.12)] hover:bg-neutral-100',
          isOpen ? 'bg-indigo-50 outline outline-1 outline-indigo-500' : 'bg-neutral-50',
        )}
      >
        <span>{formatDisplayDate(value)}</span>
        <IconChevronDown
          className={clsx('h-4 w-4 text-neutral-400 transition-transform', isOpen && 'rotate-180')}
        />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={popupRef}
            className="fixed z-[9999] w-[280px] rounded-xl bg-white p-5 shadow-xl ring-1 ring-black/10"
            style={{ top: pos.top, left: pos.left }}
          >
            {/* Header */}
            <div className="mb-5 flex items-center justify-between">
              <span className="text-base font-semibold text-neutral-900">Date</span>
              <button
                type="button"
                onClick={() => setPos(null)}
                className="rounded-lg p-1.5 hover:bg-neutral-100"
              >
                <IconClose className="h-5 w-5 text-neutral-400" />
              </button>
            </div>

            {/* Month Navigation */}
            <div className="mb-5 flex items-center justify-between">
              <button
                type="button"
                onClick={handlePrevMonth}
                className="rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-sm font-semibold text-neutral-900">{monthYear}</span>
              <button
                type="button"
                onClick={handleNextMonth}
                className="rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Days Header */}
            <div className="mb-1 grid grid-cols-7 gap-1">
              {DAYS.map((day, i) => (
                <div
                  key={i}
                  className="flex h-8 items-center justify-center text-sm font-medium text-neutral-400"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {days.map(({ date, isCurrentMonth }, i) => {
                const isPast = isPastDate(date);
                const selected = isSelected(date);
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={(e) => handleSelectDate(date, e)}
                    disabled={isPast}
                    className={clsx(
                      'flex h-8 items-center justify-center rounded-full text-sm transition-colors',
                      isPast
                        ? 'cursor-not-allowed text-neutral-200'
                        : selected
                          ? 'bg-indigo-500 font-semibold text-white'
                          : isCurrentMonth
                            ? 'font-medium text-neutral-900 hover:bg-neutral-100'
                            : 'text-neutral-300',
                    )}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
